import * as $ from './constants.ts'
import * as parse from './parse.ts'
import { type MarkdownToJSX, RuleType } from './types.ts'
import * as util from './utils.ts'

var hasKeys = util.hasKeys

// Re-export parser, types, and utils for the /html entry point
export { parser } from './parse.ts'
export { type MarkdownToJSX, RuleType } from './types.ts'
export { sanitizer, slugify } from './utils.ts'

/**
 * Escape HTML entities for text content
 * Fast path: return early if no escaping needed
 */
function escapeHtml(text: string): string {
  if (!text) {
    return text
  }
  // Use SIMD-accelerated indexOf to find earliest escapable char
  var i = text.indexOf('&')
  var lt = text.indexOf('<')
  if (lt !== -1 && (i === -1 || lt < i)) {
    i = lt
  }
  var gt = text.indexOf('>')
  if (gt !== -1 && (i === -1 || gt < i)) {
    i = gt
  }
  var qt = text.indexOf('"')
  if (qt !== -1 && (i === -1 || qt < i)) {
    i = qt
  }
  if (i === -1) {
    return text
  }
  // Build result from first escapable char with charCode replacement loop
  var result = text.slice(0, i)
  var last = i
  var len = text.length
  while (i < len) {
    var code = text.charCodeAt(i)
    if (code <= 62) {
      if (code === $.CHAR_AMPERSAND) {
        if (i > last) {
          result += text.slice(last, i)
        }
        result += '&amp;'
        last = i + 1
      } else if (code === $.CHAR_LT) {
        if (i > last) {
          result += text.slice(last, i)
        }
        result += '&lt;'
        last = i + 1
      } else if (code === $.CHAR_GT) {
        if (i > last) {
          result += text.slice(last, i)
        }
        result += '&gt;'
        last = i + 1
      } else if (code === $.CHAR_DOUBLE_QUOTE) {
        if (i > last) {
          result += text.slice(last, i)
        }
        result += '&quot;'
        last = i + 1
      }
    }
    i++
  }
  if (last < len) {
    result += text.slice(last)
  }
  return result
}

/**
 * Escape HTML for attribute values, preserving entity references
 * Only escapes bare & that are not part of valid entities
 */
function escapeHtmlAttr(value: string): string {
  // Fast-path: check if escaping is needed before regex
  var needsEscape = false
  var i = 0
  var len = value.length
  while (i < len) {
    var code = value.charCodeAt(i)
    if (
      code === $.CHAR_AMPERSAND ||
      code === $.CHAR_LT ||
      code === $.CHAR_GT ||
      code === $.CHAR_DOUBLE_QUOTE
    ) {
      needsEscape = true
      break
    }
    i++
  }
  if (!needsEscape) {
    return value
  }

  return value
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/&(?!([a-zA-Z0-9]+|#[0-9]+|#x[0-9a-fA-F]+);)/g, '&amp;')
}

function formatAttributes(attrs: Record<string, any>): string {
  var result = ''
  for (var key in attrs) {
    var value = attrs[key]
    if (value === undefined || value === null) {
      continue
    }
    result += ' '
    if (value === true) {
      result += key
    } else if (value === '') {
      result += `${key}=""`
    } else if (key === 'style' && typeof value === 'object' && value != null) {
      var styleStr = ''
      var styleFirst = true
      for (var styleKey in value) {
        var styleValue = value[styleKey]
        if (styleValue != null) {
          if (styleFirst) {
            styleFirst = false
          } else {
            styleStr += '; '
          }
          var cssKey = ''
          for (var i = 0; i < styleKey.length; i++) {
            var code = styleKey.charCodeAt(i)
            if (code >= $.CHAR_A && code <= $.CHAR_Z) {
              cssKey += `-${String.fromCharCode(code + $.CHAR_CASE_OFFSET)}`
            } else {
              cssKey += styleKey[i]
            }
          }
          styleStr += `${cssKey}: ${styleValue}`
        }
      }
      if (styleStr) {
        result += `style="${escapeHtmlAttr(styleStr)}"`
      }
    } else if (typeof value === 'string') {
      result += `${key}="${escapeHtmlAttr(value)}"`
    } else if (typeof value === 'number') {
      result += `${key}="${value}"`
    }
  }
  return result
}

/**
 * Override configuration for HTML tags or custom components in HTML output
 */
export type HTMLOverride =
  | {
      component?: string
      props?: Record<string, string | number | boolean>
    }
  | string

/**
 * Map of HTML tags and custom components to their override configurations
 */
export type HTMLOverrides = {
  [tag in MarkdownToJSX.HTMLTags]?: HTMLOverride
} & {
  [customComponent: string]: HTMLOverride
}

/**
 * HTML compiler options
 */
export type HTMLOptions = Omit<
  MarkdownToJSX.Options,
  'createElement' | 'wrapperProps' | 'overrides' | 'renderRule'
> & {
  /** Reference definitions for link resolution */
  refs?: { [key: string]: { target: string; title: string | undefined } }
  /** Custom rendering function for AST rules */
  renderRule?: (
    next: () => string,
    node: MarkdownToJSX.ASTNode,
    renderChildren: (children: MarkdownToJSX.ASTNode[]) => string,
    state: MarkdownToJSX.State
  ) => string
  /** Override configurations for HTML tags */
  overrides?: HTMLOverrides
  /** Props for wrapper element */
  wrapperProps?: Record<string, string | number | boolean>
  /** Internal flag for wrapper auto-detection */
  wrapperWasAutoSet?: boolean
}

// Render context — created once per astToHTML call, shared across all recursive renders
interface _Ctx {
  sanitize: (url: string, tag: string, attr: string) => string | null
  slug: (input: string, defaultFn: typeof util.slugify) => string
  refs: { [key: string]: { target: string; title: string | undefined } }
  overrides: HTMLOverrides
  hasOverrides: boolean
  preserveFrontmatter: boolean
  tagfilter: boolean
  forceInline: boolean
  renderRule?: HTMLOptions['renderRule']
}

var _emptyObj: Record<string, any> = {}

function shouldSkipNode(
  node: MarkdownToJSX.ASTNode,
  preserveFrontmatter: boolean
): boolean {
  return (
    node.type === RuleType.footnote ||
    node.type === RuleType.ref ||
    (node.type === RuleType.frontmatter && !preserveFrontmatter)
  )
}

function _mergeAttrs(
  base: Record<string, any> | undefined,
  overrideProps: Record<string, any>
): Record<string, any> {
  if (!hasKeys(overrideProps)) {
    return base || _emptyObj
  }
  var merged = base ? { ...base } : {}
  for (var key in overrideProps) {
    merged[key] = overrideProps[key]
  }
  return merged
}

function _renderTag(
  defaultTag: string,
  children: string,
  ctx: _Ctx,
  attrs?: Record<string, any>
): string {
  if (!ctx.hasOverrides) {
    if (!(attrs && hasKeys(attrs))) {
      return `<${defaultTag}>${children}</${defaultTag}>`
    }
    return (
      '<' +
      defaultTag +
      formatAttributes(attrs) +
      '>' +
      children +
      '</' +
      defaultTag +
      '>'
    )
  }
  var tag = util.getTag(defaultTag, ctx.overrides)
  var overrideProps = util.getOverrideProps(defaultTag, ctx.overrides)
  if (!(attrs && hasKeys(attrs))) {
    return (
      '<' +
      tag +
      formatAttributes(overrideProps) +
      '>' +
      children +
      '</' +
      tag +
      '>'
    )
  }
  var finalAttrs = { ...overrideProps }
  for (var aKey in attrs) {
    finalAttrs[aKey] = attrs[aKey]
  }
  return `<${tag}${formatAttributes(finalAttrs)}>${children}</${tag}>`
}

function _renderChildren(nodes: MarkdownToJSX.ASTNode[], ctx: _Ctx): string {
  var result = ''
  if (ctx.renderRule) {
    for (var i = 0; i < nodes.length; i++) {
      result += _renderNodeEntry(nodes[i], { key: i }, ctx)
    }
  } else {
    for (var i = 0; i < nodes.length; i++) {
      var node = nodes[i]
      if (node.type === RuleType.text) {
        if (node.text) {
          result += escapeHtml(node.text)
        }
      } else {
        result += _renderNode(node, ctx)
      }
    }
  }
  return result
}

function _renderNodeEntry(
  node: MarkdownToJSX.ASTNode,
  state: MarkdownToJSX.State,
  ctx: _Ctx
): string {
  if (ctx.renderRule) {
    return ctx.renderRule(
      () => {
        if (
          node.type === RuleType.refCollection ||
          shouldSkipNode(node, ctx.preserveFrontmatter)
        ) {
          return ''
        }
        return _renderNode(node, ctx)
      },
      node,
      (children: MarkdownToJSX.ASTNode[]) => _renderChildren(children, ctx),
      state
    )
  }

  if (
    node.type === RuleType.refCollection ||
    shouldSkipNode(node, ctx.preserveFrontmatter)
  ) {
    return ''
  }

  return _renderNode(node, ctx)
}

function _renderNode(node: MarkdownToJSX.ASTNode, ctx: _Ctx): string {
  switch (node.type) {
    case RuleType.blockQuote: {
      var children = node.children ? _renderChildren(node.children, ctx) : ''
      if (node.alert) {
        children = `<header>${escapeHtml(node.alert)}</header>${children}`
        return _renderTag('blockquote', children, ctx, {
          class:
            'markdown-alert-' +
            ctx.slug(node.alert.toLowerCase(), util.slugify),
        })
      }
      return _renderTag('blockquote', children, ctx)
    }

    case RuleType.breakLine: {
      return '<br />\n'
    }
    case RuleType.breakThematic: {
      return '<hr />'
    }

    case RuleType.frontmatter: {
      return `<pre>${escapeHtml(node.text)}</pre>`
    }

    case RuleType.codeBlock: {
      var codeAttrs: Record<string, any> = node.attrs || {}
      if (node.lang) {
        codeAttrs = { ...codeAttrs }
        var decodedLang = util.decodeEntityReferences(node.lang)
        var existingClass =
          (codeAttrs.class as string) || (codeAttrs.className as string) || ''
        codeAttrs.class = existingClass
          ? `${existingClass} language-${decodedLang}`
          : `language-${decodedLang}`
        codeAttrs.className = undefined
      }
      var codeAttrStr = formatAttributes(codeAttrs)
      var codeText = node.text || ''
      // Known deviation from CommonMark: the spec keeps a code block's trailing
      // newline (the scanners store node.text without it). No rendered
      // difference, since a final empty line generates no line box.
      return (
        '<pre><code' +
        codeAttrStr +
        '>' +
        escapeHtml(codeText) +
        '</code></pre>'
      )
    }

    case RuleType.codeInline: {
      return `<code>${escapeHtml(node.text || '')}</code>`
    }

    case RuleType.footnoteReference: {
      var href = ctx.sanitize(node.target || '', 'a', 'href') || ''
      var text = escapeHtml(node.text || '')
      return `<a href="${escapeHtml(href)}"><sup>${text}</sup></a>`
    }

    case RuleType.gfmTask: {
      return (
        '<input' +
        (node.completed ? ' checked=""' : '') +
        ' disabled="" type="checkbox">'
      )
    }

    case RuleType.heading: {
      var level = node.level || 1
      var headingAttrs = node.id?.trim() ? { id: node.id } : undefined
      return _renderTag(
        `h${level}`,
        node.children ? _renderChildren(node.children, ctx) : '',
        ctx,
        headingAttrs
      )
    }

    case RuleType.htmlComment: {
      var htmlCommentNode = node as MarkdownToJSX.HTMLCommentNode
      if (htmlCommentNode.raw) {
        return htmlCommentNode.text
      }
      if (htmlCommentNode._endsWithGT) {
        return `<!--${node.text}>`
      }
      return `<!--${node.text}-->`
    }

    case RuleType.htmlBlock: {
      var htmlNode = node as MarkdownToJSX.HTMLNode
      var defaultTag = htmlNode.tag || 'div'
      var tag = ctx.hasOverrides
        ? util.getTag(defaultTag, ctx.overrides)
        : defaultTag
      var overrideProps = ctx.hasOverrides
        ? util.getOverrideProps(defaultTag, ctx.overrides)
        : _emptyObj
      var attrsStr: string
      if (htmlNode._rawAttrs === undefined) {
        attrsStr = formatAttributes(_mergeAttrs(htmlNode.attrs, overrideProps))
      } else {
        var rawAttrsValue = htmlNode._rawAttrs
        var needsLeadingSpace =
          rawAttrsValue.length > 0 && rawAttrsValue.charCodeAt(0) > $.CHAR_SPACE
        attrsStr =
          (needsLeadingSpace ? ' ' : '') +
          rawAttrsValue +
          (hasKeys(overrideProps)
            ? ` ${formatAttributes(overrideProps).trim()}`
            : '')
      }
      if (ctx.tagfilter && util.shouldFilterTag(tag)) {
        var filtered = util.getFilteredTagEmit(htmlNode)
        if (filtered.kind === 'literal') {
          return util.applyTagFilterToText(filtered.literal)
        }
        return (
          util.applyTagFilterToText(filtered.open) +
          (htmlNode.children ? _renderChildren(htmlNode.children, ctx) : '') +
          util.applyTagFilterToText(filtered.close)
        )
      }
      var rawOuter = htmlNode._rawOuter
      if (htmlNode._isClosingTag && htmlNode._rawBody) {
        var closerTrailing = ctx.tagfilter
          ? util.applyTagFilterToText(htmlNode._rawBody)
          : htmlNode._rawBody
        return `</${tag}>${closerTrailing}`
      }
      if (htmlNode._verbatim && rawOuter !== undefined) {
        // Parser-verified full outer span (own opening tag through own
        // closing tag, or through end of input when none exists); emit
        // as-is, no synthesized wrapper needed.
        return ctx.tagfilter ? util.applyTagFilterToText(rawOuter) : rawOuter
      }
      if (htmlNode._verbatim && !htmlNode._isClosingTag) {
        var tagLower = tag.toLowerCase()
        var isType1Block = parse.isType1Block(tagLower)
        if (isType1Block) {
          // The parser always supplies _rawClose (or _rawOuter, handled
          // above) for real Type 1 output; a missing close here only
          // happens on a hand-built AST, so synthesize the tag's own
          // closer rather than leaving the element unclosed.
          var type1BodyAndClose =
            (htmlNode._rawBody || '') + (htmlNode._rawClose || `</${tag}>`)
          var type1Content = ctx.tagfilter
            ? util.applyTagFilterToText(type1BodyAndClose)
            : type1BodyAndClose
          return `<${tag}${attrsStr}>${type1Content}`
        }
        var bodyAndClose =
          (htmlNode._rawBody || '') + (htmlNode._rawClose || '')
        var textContent = ctx.tagfilter
          ? util.applyTagFilterToText(bodyAndClose)
          : bodyAndClose
        // Parser sets _emitOwnClose when rawBody represents a closed HTML
        // block nested inside another HTML block. rawBody may carry trailing
        // same-line siblings (kept for the React re-parse split), so slice
        // at the own close tag and always emit our own </tag> (#862).
        if (htmlNode._emitOwnClose) {
          var ownCloseIdx67 = textContent.indexOf(`</${tagLower}>`)
          var body67 =
            ownCloseIdx67 === -1
              ? textContent
              : textContent.slice(0, ownCloseIdx67)
          return `<${tag}${attrsStr}>${body67}</${tag}>`
        }
        var trimmedStart = 0
        while (
          trimmedStart < textContent.length &&
          textContent.charCodeAt(trimmedStart) === $.CHAR_SPACE
        ) {
          trimmedStart++
        }
        return (
          '<' +
          tag +
          attrsStr +
          '>' +
          (trimmedStart > 0
            ? textContent.slice(trimmedStart)
            : (htmlNode._rawBody || '').trim()
              ? textContent
              : '')
        )
      }
      if (htmlNode._rawBody) {
        var nonVerbatimContent = ctx.tagfilter
          ? util.applyTagFilterToText(htmlNode._rawBody)
          : htmlNode._rawBody
        return `<${tag}${attrsStr}>${nonVerbatimContent}</${tag}>`
      }
      if (util.isVoidElement(tag)) {
        return `<${tag}${attrsStr}>`
      }
      var children = htmlNode.children
        ? _renderChildren(htmlNode.children, ctx)
        : ''
      if (htmlNode._isClosingTag) {
        return `</${tag}>${children}`
      }
      // Structurally-closed elements always emit </tag>, even when empty.
      // _verbatim is raw source pass-through (e.g. unclosed <div>); only then
      // do we suppress the close, and only when children is whitespace-only.
      if (!htmlNode._verbatim) {
        return `<${tag}${attrsStr}>${children}</${tag}>`
      }
      for (var hci = 0; hci < children.length; hci++) {
        var hcc = children.charCodeAt(hci)
        if (
          hcc !== $.CHAR_SPACE &&
          hcc !== $.CHAR_TAB &&
          hcc !== $.CHAR_NEWLINE &&
          hcc !== $.CHAR_CR
        ) {
          return `<${tag}${attrsStr}>${children}</${tag}>`
        }
      }
      return `<${tag}${attrsStr}>${children}`
    }

    case RuleType.htmlSelfClosing: {
      var scNode = node as MarkdownToJSX.HTMLSelfClosingNode
      var scDefaultTag = scNode.tag || 'div'
      var scTag = ctx.hasOverrides
        ? util.getTag(scDefaultTag, ctx.overrides)
        : scDefaultTag
      if (ctx.tagfilter && util.shouldFilterTag(scTag)) {
        var filteredSc = util.getFilteredTagEmit(scNode)
        return util.applyTagFilterToText(
          filteredSc.kind === 'literal'
            ? filteredSc.literal
            : filteredSc.open + (filteredSc.close || '')
        )
      }
      if (scNode._isClosingTag) {
        return scNode._rawClose || `</${scTag}>`
      }
      if (scNode._rawOpen) {
        return scNode._rawOpen
      }
      var scOverrideProps = ctx.hasOverrides
        ? util.getOverrideProps(scDefaultTag, ctx.overrides)
        : _emptyObj
      var scMergedAttrs = _mergeAttrs(scNode.attrs, scOverrideProps)
      var scAttrsStr = formatAttributes(scMergedAttrs)
      return `<${scTag}${scAttrsStr} />`
    }

    case RuleType.image: {
      var imgTag = ctx.hasOverrides ? util.getTag('img', ctx.overrides) : 'img'
      var imgOverrideProps = ctx.hasOverrides
        ? util.getOverrideProps('img', ctx.overrides)
        : _emptyObj
      var src = ctx.sanitize(node.target || '', 'img', 'src') || ''
      var imgAttrs: Record<string, any> = {
        ...imgOverrideProps,
        alt: node.alt || '',
      }
      if (node.title) {
        imgAttrs.title = node.title
      }
      return (
        '<' +
        imgTag +
        ' src="' +
        escapeHtml(src) +
        '"' +
        formatAttributes(imgAttrs) +
        ' />'
      )
    }

    case RuleType.link: {
      var linkTag = ctx.hasOverrides ? util.getTag('a', ctx.overrides) : 'a'
      var linkOverrideProps = ctx.hasOverrides
        ? util.getOverrideProps('a', ctx.overrides)
        : _emptyObj
      var linkAttrs: Record<string, any> = hasKeys(linkOverrideProps)
        ? { ...linkOverrideProps }
        : {}
      if (node.target != null) {
        // Sanitize before encode (same order as the JSX compilers) so a
        // dangerous direct-AST target never reaches href=.
        var linkHref = util.sanitizeAndEncodeUrlTarget(
          util.decodeEntityReferences(node.target),
          ctx.sanitize,
          'a',
          'href'
        )
        if (linkHref != null) {
          linkAttrs.href = linkHref
        }
      }
      if (node.title) {
        linkAttrs.title = util.decodeEntityReferences(node.title)
      }
      return (
        '<' +
        linkTag +
        formatAttributes(linkAttrs) +
        '>' +
        (node.children ? _renderChildren(node.children, ctx) : '') +
        '</' +
        linkTag +
        '>'
      )
    }

    case RuleType.table: {
      var tableNode = node as MarkdownToJSX.TableNode
      var alignments = tableNode.align || []
      var header = ''
      var headerCells = tableNode.header || []
      for (var hi = 0; hi < headerCells.length; hi++) {
        var align = alignments[hi]
        header +=
          '<th' +
          (align ? ` align="${align}"` : '') +
          '>' +
          _renderChildren(headerCells[hi], ctx) +
          '</th>'
      }
      var rows = ''
      var tableRows = tableNode.cells || []
      for (var ri = 0; ri < tableRows.length; ri++) {
        var row = tableRows[ri] || []
        rows += '<tr>'
        for (var ci = 0; ci < row.length; ci++) {
          var align = alignments[ci]
          rows +=
            '<td' +
            (align ? ` align="${align}"` : '') +
            '>' +
            _renderChildren(row[ci], ctx) +
            '</td>'
        }
        rows += '</tr>'
      }
      if (ctx.hasOverrides) {
        var tblTag = util.getTag('table', ctx.overrides)
        var tblAttrStr = formatAttributes(
          util.getOverrideProps('table', ctx.overrides)
        )
        return (
          '<' +
          tblTag +
          tblAttrStr +
          '><thead><tr>' +
          header +
          '</tr></thead>' +
          (rows ? `<tbody>${rows}</tbody>` : '') +
          '</' +
          tblTag +
          '>'
        )
      }
      return (
        '<table><thead><tr>' +
        header +
        '</tr></thead>' +
        (rows ? `<tbody>${rows}</tbody>` : '') +
        '</table>'
      )
    }

    case RuleType.text:
      return escapeHtml(node.text || '')

    case RuleType.textFormatted: {
      return _renderTag(
        node.tag || 'strong',
        node.children ? _renderChildren(node.children, ctx) : '',
        ctx
      )
    }

    case RuleType.orderedList:
    case RuleType.unorderedList: {
      var items = ''
      var listItems = node.items || []
      for (var li = 0; li < listItems.length; li++) {
        items += `<li>${_renderChildren(listItems[li], ctx)}</li>`
      }
      var listTag = node.type === RuleType.orderedList ? 'ol' : 'ul'
      var listAttrs =
        node.type === RuleType.orderedList &&
        node.start != null &&
        node.start !== 1
          ? { start: node.start }
          : undefined
      return _renderTag(listTag, items, ctx, listAttrs)
    }

    case RuleType.paragraph: {
      if (ctx.forceInline) {
        return node.children ? _renderChildren(node.children, ctx) : ''
      }
      var children = node.children ? _renderChildren(node.children, ctx) : ''
      // Per CommonMark: collapse trailing spaces before newlines (soft line breaks)
      // Fast-path: skip collapsing if no newline in rendered output (most paragraphs)
      var idx = 0
      if (children.indexOf('\n') !== -1) {
        // Use indexOf to jump between occurrences (SIMD) instead of scanning every char
        // Track tag boundaries to avoid collapsing inside HTML attribute values
        var result = ''
        var segStart = 0
        var searchFrom = 0
        var inTag = false
        var quoteCount = 0
        while ((idx = children.indexOf(' \n', searchFrom)) !== -1) {
          // Update tag tracking only in the gap since last search
          for (var j = searchFrom; j < idx; j++) {
            var code = children.charCodeAt(j)
            if (code === $.CHAR_LT) {
              inTag = true
              quoteCount = 0
            } else if (code === $.CHAR_GT) {
              inTag = false
              quoteCount = 0
            } else if (inTag && code === $.CHAR_DOUBLE_QUOTE) {
              quoteCount++
            }
          }
          if (inTag && quoteCount % 2 === 1) {
            // Inside quoted attribute — preserve
            searchFrom = idx + 2
          } else {
            result += `${children.slice(segStart, idx)}\n`
            segStart = idx + 2
            searchFrom = segStart
          }
        }
        if (segStart > 0) {
          if (segStart < children.length) {
            result += children.slice(segStart)
          }
          children = result
        }
      } // end if (indexOf('\n'))

      if (!ctx.hasOverrides) {
        return `<p>${children}</p>`
      }
      var attrsStr = formatAttributes(util.getOverrideProps('p', ctx.overrides))
      var tag = util.getTag('p', ctx.overrides)
      return `<${tag}${attrsStr}>${children}</${tag}>`
    }

    default:
      return ''
  }
}

/**
 * Convert AST nodes to HTML string
 *
 * @param nodes - Array of AST nodes to render
 * @param options - HTML compiler options
 * @returns HTML string
 */
export function astToHTML(
  nodes: MarkdownToJSX.ASTNode[],
  options: HTMLOptions = {}
): string {
  var sanitize = options.sanitizer || util.sanitizer
  var slug = options.slugify || util.slugify
  var refs = options.refs || {}
  var overrides = options.overrides || {}

  // Extract refs from reference collection node and filter non-renderable nodes
  var refsFromAst: {
    [key: string]: { target: string; title: string | undefined }
  } = {}
  var nonRefCollectionNodes: MarkdownToJSX.ASTNode[] = []
  var foundRefCollection = false
  for (var i = 0; i < nodes.length; i++) {
    var node = nodes[i]
    if (node.type === RuleType.refCollection && !foundRefCollection) {
      refsFromAst = (node as MarkdownToJSX.ReferenceCollectionNode).refs || {}
      foundRefCollection = true
      continue
    }
    if (shouldSkipNode(node, Boolean(options.preserveFrontmatter))) {
      continue
    }
    nonRefCollectionNodes.push(node)
  }
  for (var key in refsFromAst) {
    refs[key] = refsFromAst[key]
  }

  // Create render context once — shared across all recursive renders
  var ctx: _Ctx = {
    sanitize,
    slug,
    refs,
    overrides,
    hasOverrides: hasKeys(overrides),
    preserveFrontmatter: Boolean(options.preserveFrontmatter),
    tagfilter: util.tagfilterEnabled(options),
    forceInline: Boolean(options.forceInline),
    renderRule: options.renderRule,
  }

  var content = ''
  if (Array.isArray(nodes)) {
    if (ctx.renderRule) {
      for (var ci = 0; ci < nonRefCollectionNodes.length; ci++) {
        content += _renderNodeEntry(
          nonRefCollectionNodes[ci],
          {
            key: ci,
            refs,
          },
          ctx
        )
      }
    } else {
      for (var ci = 0; ci < nonRefCollectionNodes.length; ci++) {
        content += _renderNode(nonRefCollectionNodes[ci], ctx)
      }
    }
  } else {
    content = _renderNodeEntry(nodes, { refs }, ctx)
  }

  // Extract and render footnotes
  var footnoteFooter = ''
  for (var key in refs) {
    if (key.charCodeAt(0) === $.CHAR_CARET) {
      if (!footnoteFooter) {
        footnoteFooter = '<footer>'
      }
      var id = key.slice(1)
      var parsed = parse.parseMarkdown(
        refs[key].target,
        { inline: true, refs },
        parse.toParseOptions(options)
      )
      var filtered: MarkdownToJSX.ASTNode[] = []
      for (var pi = 0; pi < parsed.length; pi++) {
        if (parsed[pi].type !== RuleType.refCollection) {
          filtered.push(parsed[pi])
        }
      }
      var footnoteCtx: _Ctx = { ...ctx, refs: {}, forceInline: true }
      var footnoteContent = _renderChildren(filtered, footnoteCtx)
      footnoteFooter +=
        '<div id="' +
        escapeHtmlAttr(slug(id, util.slugify)) +
        '">' +
        escapeHtml(id) +
        ': ' +
        footnoteContent +
        '</div>'
    }
  }
  if (footnoteFooter) {
    footnoteFooter += '</footer>'
  }

  // Handle wrapper options
  if (options.wrapper === null) {
    return content + footnoteFooter
  }

  // Determine if content should be wrapped (only when explicitly requested)
  var hasMultipleChildren = nonRefCollectionNodes.length > 1
  var hasExplicitWrapper = options.wrapper != null
  var wrapperWasExplicit =
    hasExplicitWrapper && typeof options.wrapper === 'string'
  var shouldWrap =
    options.forceWrapper ||
    (options.forceInline && hasExplicitWrapper) ||
    (hasMultipleChildren && hasExplicitWrapper)

  if (!shouldWrap) {
    return content + footnoteFooter
  }

  // Extract paragraph content when forceInline or forceWrapper with explicit wrapper
  var contentToWrap = content
  if (
    (options.forceInline ||
      (options.forceWrapper &&
        wrapperWasExplicit &&
        !options.wrapperWasAutoSet)) &&
    !hasMultipleChildren &&
    nonRefCollectionNodes.length === 1 &&
    nonRefCollectionNodes[0].type === RuleType.paragraph
  ) {
    var paragraphNode = nonRefCollectionNodes[0] as MarkdownToJSX.ParagraphNode
    if (paragraphNode.children) {
      var inlineCtx: _Ctx = { ...ctx, refs: {}, forceInline: true }
      contentToWrap = _renderChildren(paragraphNode.children, inlineCtx)
    }
  }

  // Determine wrapper tag
  var wrapperTag =
    typeof options.wrapper === 'string'
      ? options.wrapper
      : options.forceInline
        ? 'span'
        : 'div'

  var wrapperAttrs = ''
  if (options.wrapperProps) {
    var sanitizedProps: Record<string, string> = {}
    for (var wpKey in options.wrapperProps) {
      var wpValue = options.wrapperProps[wpKey]
      if (wpValue != null) {
        var sanitized = sanitize(String(wpValue), wrapperTag, wpKey)
        if (sanitized != null) {
          sanitizedProps[wpKey] = sanitized
        }
      }
    }
    wrapperAttrs = formatAttributes(sanitizedProps)
  }

  return (
    '<' +
    wrapperTag +
    wrapperAttrs +
    '>' +
    contentToWrap +
    footnoteFooter +
    '</' +
    wrapperTag +
    '>'
  )
}

/**
 * Compiler function that parses markdown and renders to HTML string
 * Convenience function that combines parser() and html()
 *
 * @param markdown - Markdown string to compile
 * @param options - HTML compiler options
 * @returns HTML string
 */
export function compiler(markdown: string, options?: HTMLOptions): string {
  var inline = options?.forceInline
  var ast = parse.parser(markdown, { ...options, forceInline: inline })
  var htmlOptions: HTMLOptions = {
    ...options,
    forceInline: inline,
  } as HTMLOptions
  var wrapperWasAutoSet = false
  if (options?.wrapper === undefined) {
    var nonRefCount = 0
    for (var ni = 0; ni < ast.length; ni++) {
      var n = ast[ni]
      if (
        n.type !== RuleType.refCollection &&
        n.type !== RuleType.footnote &&
        n.type !== RuleType.ref &&
        (n.type !== RuleType.frontmatter || !options?.preserveFrontmatter)
      ) {
        nonRefCount++
      }
    }
    if (nonRefCount > 1 || options?.forceWrapper) {
      htmlOptions.wrapper = 'div'
      wrapperWasAutoSet = true
    } else if (inline && nonRefCount > 0) {
      htmlOptions.wrapper = 'span'
      wrapperWasAutoSet = true
    }
  }
  htmlOptions.wrapperWasAutoSet = wrapperWasAutoSet
  return astToHTML(ast, htmlOptions)
}
